import React, { FC, Suspense, useEffect, useState } from 'react';
import { t } from '@lingui/macro';
import { Trans } from '@lingui/react';

import { Button, Flex, Image, Text } from '@rover/kibble/core';
import { useTheme } from '@rover/kibble/themes';
import StarRating from '@rover/react-lib/src/components/formFields/StarRating';
import useDeviceContext from '@rover/react-lib/src/context/useDeviceContext';
import usePageContext from '@rover/react-lib/src/context/usePageContext';
import favIcon from '@rover/react-lib/src/images/rover-favicon.png';
import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';
import { CookiePurpose, createCookie, getCookie } from '@rover/rsdk/src/modules/Network/cookies';
import useCurrentUserShim from '@rover/shared/js/hooks/useCurrentUserShim';
import useFrontendConfigurationShim from '@rover/shared/js/hooks/useFrontendConfigurationShim';
import { getRouteUrlContextless } from '@rover/utilities/deeplink';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';

const RATING = 5;
const HIDE_BANNER_COOKIE = 'appBanner';
const HIDE_BANNER_COOKIE_DAYS = 90;

const ANDROID_REVIEW_COUNT = '23k';
const IOS_REVIEW_COUNT = '359.5k';

const AppInstallBanner: FC = () => {
  const theme = useTheme();
  const { firstHttpReferrer = null, utmParams = {} } = useCurrentUserShim();
  const { branchKey, supportsMobileApp, isFlagActive } = useFrontendConfigurationShim();
  const [hidden, setHidden] = useState(false);

  const { isAndroid, isMobile, isEmbedded } = useDeviceContext();
  const { suppressAppInstallBanner } = usePageContext();
  const shouldSuppressAppInstallBanner =
    !suppressAppInstallBanner || typeof suppressAppInstallBanner === 'boolean'
      ? suppressAppInstallBanner
      : isFlagActive(suppressAppInstallBanner);

  const reviewCount = isAndroid ? ANDROID_REVIEW_COUNT : IOS_REVIEW_COUNT;

  useEffect(() => {
    if (process.env.JS_ENV_CLIENT) {
      const cookie = getCookie(HIDE_BANNER_COOKIE);
      const shouldHide = cookie === 'true';
      // - Always show banner on server
      // - Conditionally remove banner based on appBanner cookie on Cloudflare
      // - Conditionally show banner based on appBanner cookie on client
      setHidden(shouldHide);

      // Using isMobile attempts to avoid firing the event when the
      // banner is rendered, but hidden, on a desktop viewport.
      if (
        !shouldHide &&
        isMobile &&
        !isEmbedded &&
        supportsMobileApp &&
        !shouldSuppressAppInstallBanner
      ) {
        fireDataLayerEvent({
          event: 'navigation-app-install-banner-shown',
          nonInteractive: true,
        });
      }
    }
  }, []);

  if (shouldSuppressAppInstallBanner) return null;

  const closeBanner = (): void => {
    setHidden(true);
    createCookie(
      CookiePurpose.StrictlyNecessary,
      HIDE_BANNER_COOKIE,
      'true',
      HIDE_BANNER_COOKIE_DAYS
    );
  };

  const handleOpenClicked = (): void => {
    if (!branchKey) {
      captureError(new Error('Branch key not found'));
      return;
    }

    const deeplink = getRouteUrlContextless(branchKey, utmParams, firstHttpReferrer);

    fireDataLayerEvent({
      event: 'mobile-web-app-download-install',
    });

    if (process.env.JS_ENV_CLIENT) {
      // @ts-expect-error: window.location can be assigned
      window.location = deeplink;
    }
  };

  // These criteria must be edge cache safe (i.e. no user agent sniffing)
  if (isEmbedded || hidden || !supportsMobileApp) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <div edge-id="app-install-banner">
        <Flex
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          flexGrow="1"
          height="70px"
          p="2x"
          borderBottom={`1px solid ${theme.colors.border.primary.toString()}`}
          display={['flex', 'none']}
        >
          <Flex alignItems="center">
            <Button
              onClick={closeBanner}
              p="0x"
              mr="2x"
              color={theme.colors.text.tertiary.toString()}
              fontSize="18px"
              sx={{ border: 'none', background: 'none', boxShadow: 'none' }}
            >
              ×
            </Button>
            <Image width={50} height={50} src={favIcon} description={t`Rover`} />
            <Flex flexDirection="column" ml="1x">
              <strong>
                <Trans id="Rover App" />
              </strong>
              <StarRating rating={RATING} />
              <Text textColor="tertiary" size="100">
                <span edge-id="app-install-banner-review-count">{`${reviewCount}+ `}</span>
                <Trans id="Reviews" />
              </Text>
            </Flex>
          </Flex>
          <div>
            <Button variant="primary" onClick={handleOpenClicked} size="small">
              <Trans id="Open" />
            </Button>
          </div>
        </Flex>
      </div>
    </Suspense>
  );
};

export default AppInstallBanner;
